import { useMemo, useState, useRef, useEffect, type SVGProps } from "react";
import { Input, cn } from "@racoon/ui";
import { Search, X } from "lucide-react";
import { useT, Namespace } from "@racoon/i18n";
import {
  IcCar,
  IcLocation,
  IcGarage,
  IcDisposalSite,
  IcHighPriority,
  BrandIcWasteField,
} from "@racoon/icons";
import type { ConnectProto } from "@racoon/api";
import type { EnrichedPickupLocation } from "./map.dashboard";

import { normalizeForSearch } from "@racoon/lib";
type GovTruckInfo = ConnectProto.GovTruckInfo;
type GovDepotInfo = ConnectProto.GovDepotInfo;
type SuspiciousLocation = ConnectProto.SuspiciousLocation;
type Location = ConnectProto.Location;

/** A flattened, searchable view over the map's five entity kinds. */
export type MapSearchResult = {
  key: string;
  label: string;
  sublabel: string;
  lat: number;
  lon: number;
  kind: "truck" | "pickup" | "disposalSite" | "depot" | "suspiciousLocation";
  /**
   * The same mark this entity carries on the map, so results read as the map
   * does. `ref` is omitted because the generated icons are memo(forwardRef(...)),
   * whose own ref type conflicts with SVGProps' legacy one.
   */
  Icon: React.ComponentType<Omit<SVGProps<SVGSVGElement>, "ref">>;
  /** Tailwind text color for the icon, matching the marker's color. */
  iconClassName?: string;
  /** Selects the entity, opening the bottom sheet as a marker click would. */
  select: () => void;
};

interface MapSearchProps {
  pickupLocations: EnrichedPickupLocation[];
  trucks: GovTruckInfo[];
  disposalSites: Location[];
  depots: GovDepotInfo[];
  suspiciousLocations: SuspiciousLocation[];
  showTrucks: boolean;
  onPickupClick: (loc: EnrichedPickupLocation) => void;
  onTruckClick: (truck: GovTruckInfo) => void;
  onDisposalSiteClick?: (site: Location) => void;
  onDepotClick?: (depot: GovDepotInfo) => void;
  onSuspiciousLocationClick?: (location: SuspiciousLocation) => void;
  /** Recenters the map on the chosen result. */
  onFlyTo: (target: { lat: number; lon: number }) => void;
}

const MAX_RESULTS = 50;

const hasCoords = (lat?: number, lon?: number): boolean =>
  typeof lat === "number" && typeof lon === "number";

/**
 * Search over everything currently plotted on the dashboard map.
 *
 * Only entities that carry coordinates are searchable — a result has to be able
 * to recenter the map, which a coordinate-less record cannot do. Trucks are
 * excluded while their layer is hidden, so results match what is visible.
 */
export const MapSearch = ({
  pickupLocations,
  trucks,
  disposalSites,
  depots,
  suspiciousLocations,
  showTrucks,
  onPickupClick,
  onTruckClick,
  onDisposalSiteClick,
  onDepotClick,
  onSuspiciousLocationClick,
  onFlyTo,
}: MapSearchProps) => {
  const t = useT();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // The full searchable set, rebuilt only when the plotted data changes.
  const items = useMemo(() => {
    const result: MapSearchResult[] = [];

    if (showTrucks) {
      trucks.forEach((info) => {
        if (!hasCoords(info.lat, info.lon)) return;
        const plate = info.truck?.plateNumber ?? "";
        result.push({
          key: `truck-${info.truck?.id ?? plate}`,
          label: plate || String(info.truck?.id ?? ""),
          sublabel: info.serviceProvider?.name ?? t(Namespace.Govdash, "subtitle.truck"),
          lat: info.lat as number,
          lon: info.lon as number,
          kind: "truck",
          Icon: IcCar,
          select: () => onTruckClick(info),
        });
      });
    }

    pickupLocations.forEach((loc) => {
      if (!hasCoords(loc.lat, loc.lon)) return;
      result.push({
        key: `pickup-${loc.id}`,
        label: loc.name ?? String(loc.id),
        sublabel: loc._serviceProvider?.name ?? t(Namespace.Govdash, "subtitle.pickupLocation"),
        lat: loc.lat as number,
        lon: loc.lon as number,
        kind: "pickup",
        Icon: IcLocation,
        select: () => onPickupClick(loc),
      });
    });

    disposalSites.forEach((site) => {
      if (!hasCoords(site.lat, site.lon)) return;
      result.push({
        key: `disposalSite-${site.id}`,
        label: site.name ?? String(site.id),
        sublabel: t(Namespace.Govdash, "subtitle.disposalSite"),
        lat: site.lat as number,
        lon: site.lon as number,
        kind: "disposalSite",
        // Mirrors makeDisposalSiteIcon: KWC sites get their own mark.
        Icon:
          (site.name ?? "").trim().toLowerCase() === "kwc" ? IcDisposalSite : BrandIcWasteField,
        select: () => onDisposalSiteClick?.(site),
      });
    });

    depots.forEach((depot) => {
      // A depot's coordinates and name live on its `depot` Location.
      const location = depot.depot;
      if (!hasCoords(location?.lat, location?.lon)) return;
      result.push({
        key: `depot-${location?.id}`,
        label: location?.name ?? String(location?.id ?? ""),
        sublabel: depot.ownerOrganization?.name ?? t(Namespace.Govdash, "subtitle.depot"),
        lat: location?.lat as number,
        lon: location?.lon as number,
        kind: "depot",
        Icon: IcGarage,
        select: () => onDepotClick?.(depot),
      });
    });

    suspiciousLocations.forEach((item) => {
      const location = item.location;
      if (!hasCoords(location?.lat, location?.lon)) return;
      result.push({
        key: `suspicious-${location?.id}`,
        label: location?.name ?? String(location?.id ?? ""),
        sublabel: t(Namespace.Govdash, "subtitle.suspiciousSite"),
        lat: location?.lat as number,
        lon: location?.lon as number,
        kind: "suspiciousLocation",
        Icon: IcHighPriority,
        // Matches makeSuspiciousLocationIcon's red (#DC2626).
        iconClassName: "text-[#DC2626]",
        select: () => onSuspiciousLocationClick?.(item),
      });
    });

    return result;
  }, [
    t,
    showTrucks,
    trucks,
    pickupLocations,
    disposalSites,
    depots,
    suspiciousLocations,
    onTruckClick,
    onPickupClick,
    onDisposalSiteClick,
    onDepotClick,
    onSuspiciousLocationClick,
  ]);

  const filteredItems = useMemo(() => {
    const term = normalizeForSearch(searchTerm);
    if (!term) return [];
    return items
      .filter(
        (item) =>
          normalizeForSearch(item.label).includes(term) || normalizeForSearch(item.sublabel).includes(term),
      )
      .slice(0, MAX_RESULTS);
  }, [items, searchTerm]);

  const handleSelect = (item: MapSearchResult) => {
    item.select();
    onFlyTo({ lat: item.lat, lon: item.lon });
    setOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div ref={wrapperRef} className="relative w-64">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500 z-10" />
        <Input
          placeholder={t(Namespace.Common, "search")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setOpen(true)}
          size="sm"
          className="pl-9 pr-8 bg-white shadow-md"
        />
        {searchTerm && (
          <button
            type="button"
            aria-label={t(Namespace.Common, "reset")}
            onClick={() => setSearchTerm("")}
            className="absolute right-2.5 top-2.5 text-gray-500 hover:text-gray-700 z-10"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-md shadow-lg border max-h-[300px] overflow-auto z-50">
          {filteredItems.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">
              {searchTerm
                ? t(Namespace.Common, "noResults")
                : t(Namespace.Common, "startTyping")}
            </div>
          ) : (
            <div className="p-1 space-y-1">
              {filteredItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => handleSelect(item)}
                  className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-100 flex items-center gap-3 transition-colors"
                >
                  {/* viewBox is set explicitly: several generated icons ship
                      without one and would not scale to h-5/w-5. */}
                  <div className={cn("flex-shrink-0", item.iconClassName ?? "text-brand-blue")}>
                    <item.Icon viewBox="0 0 24 24" className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{item.label}</div>
                    <div className="text-xs text-gray-500 truncate">{item.sublabel}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
